import cn from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';

// eslint-disable-next-line import/default
import { IPriceForStoreItem } from '../../../types/store/store-item-price';
import ModalWindow from '../../../ui-components/Editor/ModalWindow';
import { getBundleModalGroupText } from '../../../utils/translation';

import { LandingType, LandingTypes } from '#common/model/landing';
import { StoreImageSizes } from '#common/types/blocks';
import { StoreItem, StoreUniversalItem } from '#common/types/store';
import { FiveSymbolLocale } from '#common/utils/locale';
import { DEFAULT_VC_IMAGE } from '#common/utils/store';

import './style.pcss';
import { getStyleForLazyLoad } from '../../../utils/lazy-load-img';

interface IBundleModalProps {
  sku: string;
  name: string;
  description?: string;
  img: string;
  onClose: () => void;
  price: IPriceForStoreItem;
  content: StoreItem[];
  hidden?: boolean;
  isServerSideRender: boolean;
  children: React.ReactNode;
  imageSize: StoreImageSizes;
  landingType: LandingTypes;
  locale: FiveSymbolLocale;
}

const itemsOfGroup = (items: any[], type: StoreUniversalItem) => {
  const isVirtualCurrency = type === StoreUniversalItem.VIRTUAL_CURRENCY;
  const isBundle = type === StoreUniversalItem.BUNDLE;
  return items.filter(
    (item) =>
      (isVirtualCurrency &&
        item.type === 'bundle' &&
        item.bundle_type === 'virtual_currency_package') ||
      (isBundle && item.type === 'bundle' && item.bundle_type === 'standard') ||
      (item.type === type && item.type !== 'bundle')
  );
};

const itemGroupOrder = [
  StoreUniversalItem.VIRTUAL_GOOD,
  StoreUniversalItem.VIRTUAL_CURRENCY,
  StoreUniversalItem.GAME_KEY,
  StoreUniversalItem.BUNDLE,
];

const BundleModal: React.FC<IBundleModalProps> = ({
  sku,
  name,
  description,
  img,
  onClose,
  hidden = true,
  price,
  content,
  isServerSideRender,
  children,
  imageSize,
  landingType,
  locale,
}: IBundleModalProps) => {
  const rootEl = window.document.getElementById('site-builder-editor');
  const isForVc = !!(price.vcPrice && landingType !== LandingType.TOPUP);
  const el = (
    <ModalWindow
      className='bundle-modal'
      bodyClassName={cn(
        { 'bundle-modal__body--discount': price.hasDiscount },
        'bundle-modal__body'
      )}
      isServerSideRender={isServerSideRender}
      hidden={hidden}
      closeModal={onClose}
      dataAttributes={{
        'data-sku': sku,
        'data-type': 'bundle-content-modal',
      }}
    >
      {price.hasDiscount && (
        <div className='bundle-modal__ribbon-wrapper'>
          <div className='bundle-modal__ribbon'>{`${price.discountAmount}%`}</div>
        </div>
      )}
      <div className='bundle-modal__content'>
        <div className='bundle-modal__header'>
          <div className='bundle-modal__text'>
            <h3 className='bundle-modal__name' title={name}>
              {name}
            </h3>
            <p className='bundle-modal__description'>{description}</p>
          </div>
          <div
            className={cn('bundle-modal__image-background', {
              'bundle-modal__image-background--small': description === null,
              'bundle-modal__image-background--discount': price.hasDiscount,
            })}
          >
            <img
              className={`bundle-modal__image bundle-modal__image--${imageSize}`}
              alt='bundle image'
              src={img}
            />
          </div>
        </div>
        <div className='bundle-modal__items'>
          <span className='bundle-modal__items-title'>
            {getBundleModalGroupText(locale)}
          </span>
          {itemGroupOrder.map((type) => {
            const items = itemsOfGroup(content, type);
            return (
              !!items.length && (
                <ul
                  key={sku + type}
                  className={cn('bundle-modal__group', {
                    'bundle-modal__group--discount': price.hasDiscount,
                  })}
                >
                  {itemsOfGroup(content, type)?.map((item) => (
                    <li
                      key={item.sku + item.drm_sku}
                      className='bundle-modal__item'
                    >
                      {type !== StoreUniversalItem.GAME_KEY && (
                        <div
                          className='bundle-modal__item-image lazy'
                          data-bg={`url(${item.image_url || DEFAULT_VC_IMAGE})`}
                          style={getStyleForLazyLoad(
                            isServerSideRender,
                            item.image_url || DEFAULT_VC_IMAGE
                          )}
                        />
                      )}
                      <span className='bundle-modal__item-name'>
                        {item.name}
                      </span>
                      {item.type !== StoreUniversalItem.GAME_KEY && (
                        <div className='bundle-modal__quantity'>
                          x{item?.quantity}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )
            );
          })}
        </div>
      </div>
      <div className='bundle-modal__footer'>
        <div className='bundle-modal__price'>
          {isForVc ? (
            <>
              <div className='bundle-modal__price--without-discount'>
                <img
                  className='bundle-modal__virtual-currency-image'
                  alt='image of virtual currency'
                  src={price.vcPrice?.image_url || DEFAULT_VC_IMAGE}
                />
                <span>{price.vcPrice?.amount}</span>
              </div>
              {price.hasDiscount && (
                <div className='bundle-modal__price--with-discount'>
                  <img
                    className='bundle-modal__virtual-currency-image'
                    alt='image of virtual currency'
                    src={price.vcPrice?.image_url || DEFAULT_VC_IMAGE}
                  />
                  <span>{price.vcPrice?.amount_without_discount}</span>
                </div>
              )}
            </>
          ) : (
            <>
              <span className='bundle-modal__price--without-discount'>
                {price.realPrice}
              </span>
              {price.hasDiscount && (
                <span className='bundle-modal__price--with-discount'>
                  {price.realWithoutDiscountPrice}
                </span>
              )}
            </>
          )}
        </div>
        {children}
      </div>
    </ModalWindow>
  );

  return rootEl ? ReactDOM.createPortal(el, rootEl) : null;
};

export default BundleModal;
